import { useMemo, useState } from 'react';
import { HistoryIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  ACCESS_LOG_FILTERS,
  ACCESS_LOG_FILTER_LABEL,
  AccessLogList,
  inAccessLogFilter,
  useWorkspaceAccessLog,
  type AccessLogFilter,
} from '@/features/workspaces';
import { ListCard } from '@/shared/components/ListCard';

import { matchesAccessLogEntry } from './activitySearch';

/**
 * The workspace's activity, as its own tab of Settings (spec 194 US4, spec 191,
 * service spec 157): who joined, left or changed role, visibility and name changes,
 * requests to join, and what an org admin did here with their own powers. Kept for
 * good (decision Q25), read newest first, 50 at a time.
 *
 * Drawn in the shared list card (spec 226 FR-018): its title counts the entries
 * read so far, a search finds a person or words on an entry's line, the five
 * chips sit on the same toolbar row, and the loading, failed and empty states
 * draw inside the card. Day headings and rows are `AccessLogList`'s, unchanged.
 *
 * Offered only to those who may read the log; the page decides that, so this reads
 * whenever it is rendered.
 */
export function ActivityTab({ workspaceId }: { workspaceId: string }) {
  const log = useWorkspaceAccessLog(workspaceId, true);
  const [filter, setFilter] = useState<AccessLogFilter>('all');
  const [query, setQuery] = useState('');
  const needle = query.trim();

  // The counts are of what has been read: older pages may hold more. Chips count
  // over the unsearched entries, so a reader sees where a search would lead.
  const counts = useMemo(() => {
    const result = {} as Record<AccessLogFilter, number>;
    for (const option of ACCESS_LOG_FILTERS) {
      result[option] = log.entries.filter((entry) =>
        inAccessLogFilter(entry, option),
      ).length;
    }
    return result;
  }, [log.entries]);

  const shown = useMemo(
    () =>
      log.entries.filter(
        (entry) =>
          inAccessLogFilter(entry, filter) &&
          matchesAccessLogEntry(entry, needle),
      ),
    [log.entries, filter, needle],
  );

  const state = log.isPending
    ? 'loading'
    : log.isError
      ? 'failed'
      : log.entries.length === 0
        ? 'empty'
        : 'idle';

  const showOlder = log.hasMore ? (
    <Button
      variant="ghost"
      size="sm"
      disabled={log.loadingMore}
      onClick={log.loadMore}
    >
      {log.loadingMore ? 'Loading…' : 'Show older'}
    </Button>
  ) : null;

  return (
    <ListCard<AccessLogFilter>
      title="Activity"
      count={log.entries.length}
      headingId="activity-heading"
      description="Who joined or left, changes to visibility and name, requests to join, and what an organization admin did here."
      noun={['entry', 'entries']}
      state={state}
      failedText="The activity could not be read just now."
      onRetry={log.retry}
      empty={{
        icon: HistoryIcon,
        title: 'Nothing recorded yet',
        description:
          'Who joins or leaves, role, visibility and name changes, and requests to join appear here.',
      }}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: 'Search by person or words',
        label: 'Search activity',
      }}
      chips={{
        label: 'Show activity',
        options: ACCESS_LOG_FILTERS.map((option) => ({
          value: option,
          label: ACCESS_LOG_FILTER_LABEL[option],
          count: counts[option],
        })),
        value: filter,
        onChange: setFilter,
      }}
      shown={shown.length}
      total={log.entries.length}
      noResults={{
        description: noResultsSentence(needle, filter),
        onClear: needle
          ? () => {
              setQuery('');
            }
          : undefined,
        extra: showOlder,
      }}
    >
      <AccessLogList
        entries={shown}
        label="Activity"
        hasMore={log.hasMore}
        loadingMore={log.loadingMore}
        onMore={log.loadMore}
      />
    </ListCard>
  );
}

/** What left nothing: the search, the chip, or both (spec 226 US2 scenario 2). */
function noResultsSentence(needle: string, filter: AccessLogFilter): string {
  if (needle && filter !== 'all') {
    return `Nothing read so far matches “${needle}” under ${ACCESS_LOG_FILTER_LABEL[filter]}.`;
  }
  if (needle) return `Nothing read so far matches “${needle}”.`;
  return 'None of the activity read so far is of this kind.';
}
